import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Form, Input, TextArea, Button, Checkbox, Select } from 'semantic-ui-react';
import { errorInput, searchSelectProps } from 'src/components/helpers';
import MarkdownTextarea from 'src/components/common/markdown-textarea';

const FormContainer = styled.div`
  padding: 1rem 0;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-family: 'Raleway', sans-serif;
  color: #2c3e50;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
`;

const FormField = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #2c3e50;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
`;

const FileUploadSection = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  margin-bottom: 1rem;
  background-color: #f9fafb;
  transition: border-color 0.2s;

  &:hover {
    border-color: #007bff;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileLabel = styled.label`
  cursor: pointer;
  color: #007bff;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const FileList = styled.div`
  margin-top: 1rem;
`;

const FileItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  margin-bottom: 0.5rem;
`;

const RemoveFileButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.75rem;

  &:hover {
    background: #c82333;
  }
`;

const LessonForm = ({ lesson, courses, onSave, onCancel }) => {
  const [inputs, setInputs] = useState({
    title: '',
    description: '',
    content: '',
    category: '',
    order: '',
    courseId: '',
    videoUrl: '',
    duration: '',
    status: true,
    isLocked: false,
    requiresCompletion: false,
    attachments: [],
    quiz: {
      enabled: false,
      questions: [],
      passingScore: 70,
    },
  });
  const [errorInputs, setErrorInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  useEffect(() => {
    if (lesson) {
      setInputs({
        title: lesson.title || '',
        description: lesson.description || '',
        content: lesson.content || '',
        category: lesson.category || '',
        order: lesson.order || '',
        courseId: lesson.courseId || '',
        videoUrl: lesson.videoUrl || '',
        duration: lesson.duration || '',
        status: lesson.status !== false,
        isLocked: lesson.isLocked || false,
        requiresCompletion: lesson.requiresCompletion || false,
        attachments: lesson.attachments || [],
        quiz: lesson.quiz || {
          enabled: false,
          questions: [],
          passingScore: 70,
        },
      });
      setUploadedFiles(lesson.attachments || []);
    }
  }, [lesson]);

  const handleInputs = (event) => {
    const { name, value } = event.target;
    setInputs({ ...inputs, [name]: value });
    if (errorInputs && errorInputs[name]) {
      setErrorInputs({ ...errorInputs, [name]: false });
    }
  };

  const handleSelect = (event, { name, value }) => {
    setInputs({ ...inputs, [name]: value });
    if (errorInputs && errorInputs[name]) {
      setErrorInputs({ ...errorInputs, [name]: false });
    }
  };

  const handleCheckbox = (name) => {
    setInputs({ ...inputs, [name]: !inputs[name] });
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
    }));
    setUploadedFiles([...uploadedFiles, ...newFiles]);
  };

  const removeFile = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
  };

  const verifyForm = () => {
    const mandatoryInputs = ['title', 'description', 'category', 'order', 'courseId'];
    let newErrorInputs = {};

    mandatoryInputs.forEach((input) => {
      newErrorInputs = errorInput({
        errorInputs: newErrorInputs,
        input: { name: input, value: inputs[input] },
      });
    });

    // Validate order is a number
    if (inputs.order && isNaN(Number(inputs.order))) {
      newErrorInputs.order = true;
    }

    // Validate duration format
    if (inputs.duration && !/^\d+\s*(minutes?|hours?)$/i.test(inputs.duration)) {
      newErrorInputs.duration = true;
    }

    setErrorInputs(newErrorInputs);
    return Object.values(newErrorInputs).includes(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (verifyForm()) return;

    setLoading(true);
    try {
      const lessonData = {
        ...inputs,
        order: Number(inputs.order),
        attachments: (uploadedFiles || []).map((file) => ({
          name: file.name,
          size: file.size,
          type: file.type,
          url: file.url || null,
        })),
      };

      await onSave(lessonData);
    } catch (error) {
      console.error('Error saving lesson:', error);
    } finally {
      setLoading(false);
    }
  };

  const courseOptions = (courses || []).map((course) => ({
    key: course.id,
    value: course.id,
    text: course.title,
  }));

  const categoryOptions = [
    { key: 'introduction', value: 'introduction', text: 'Introducción' },
    { key: 'theory', value: 'theory', text: 'Teoría' },
    { key: 'practice', value: 'practice', text: 'Práctica' },
    { key: 'assessment', value: 'assessment', text: 'Evaluación' },
    { key: 'resources', value: 'resources', text: 'Recursos' },
  ];

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Show loading state if courses are not loaded yet
  if (!courses || courses.length === 0) {
    return (
      <FormContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Cargando cursos...</p>
        </div>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Form onSubmit={handleSubmit}>
        <FormSection>
          <SectionTitle>Información Básica</SectionTitle>

          <FormField>
            <Label>Curso *</Label>
            <Select
              placeholder="Selecciona el curso"
              className={errorInputs.courseId ? 'error' : ''}
              fluid
              {...searchSelectProps({
                name: 'courseId',
                value: inputs.courseId,
                handler: handleSelect,
                options: courseOptions,
              })}
            />
            {errorInputs.courseId && <ErrorMessage>Debes seleccionar un curso</ErrorMessage>}
          </FormField>

          <FormField>
            <Label>Título de la Lección *</Label>
            <Input
              name="title"
              value={inputs.title}
              onChange={handleInputs}
              placeholder="Ingresa el título de la lección"
              className={errorInputs.title ? 'error' : ''}
              fluid
            />
            {errorInputs.title && <ErrorMessage>El título es obligatorio</ErrorMessage>}
          </FormField>

          <FormField>
            <Label>Descripción *</Label>
            <TextArea
              name="description"
              value={inputs.description}
              onChange={handleInputs}
              placeholder="Descripción de la lección"
              className={errorInputs.description ? 'error' : ''}
              rows={3}
            />
            {errorInputs.description && <ErrorMessage>La descripción es obligatoria</ErrorMessage>}
          </FormField>

          <FormField>
            <Label>Categoría *</Label>
            <Select
              placeholder="Selecciona la categoría"
              className={errorInputs.category ? 'error' : ''}
              fluid
              {...searchSelectProps({
                name: 'category',
                value: inputs.category,
                handler: handleSelect,
                options: categoryOptions,
              })}
            />
            {errorInputs.category && <ErrorMessage>Debes seleccionar una categoría</ErrorMessage>}
          </FormField>

          <FormField>
            <Label>Orden *</Label>
            <Input
              name="order"
              value={inputs.order}
              onChange={handleInputs}
              placeholder="1, 2, 3..."
              type="number"
              min="1"
              className={errorInputs.order ? 'error' : ''}
              fluid
            />
            {errorInputs.order && <ErrorMessage>El orden debe ser un número válido</ErrorMessage>}
          </FormField>
        </FormSection>

        <FormSection>
          <SectionTitle>Contenido de la Lección</SectionTitle>

          <FormField>
            <Label>Contenido (Markdown)</Label>
            <MarkdownTextarea
              inputs={inputs}
              name="content"
              label="Contenido de la lección"
              handleInputs={handleInputs}
              errorInputs={errorInputs}
              loading={loading}
            />
          </FormField>

          <FormField>
            <Label>URL del Video</Label>
            <Input
              name="videoUrl"
              value={inputs.videoUrl}
              onChange={handleInputs}
              placeholder="https://www.youtube.com/watch?v=..."
              fluid
            />
          </FormField>

          <FormField>
            <Label>Duración</Label>
            <Input
              name="duration"
              value={inputs.duration}
              onChange={handleInputs}
              placeholder="Ej: 15 minutes, 1 hour"
              className={errorInputs.duration ? 'error' : ''}
              fluid
            />
            {errorInputs.duration && (
              <ErrorMessage>Formato inválido. Usa: "15 minutes", "1 hour"</ErrorMessage>
            )}
          </FormField>
        </FormSection>

        <FormSection>
          <SectionTitle>Archivos Adjuntos</SectionTitle>

          <FileUploadSection>
            <FileLabel htmlFor="file-upload">
              <FileInput
                id="file-upload"
                type="file"
                multiple
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
              />
              Haz clic aquí para subir archivos o arrastra archivos aquí
            </FileLabel>
            <p style={{ marginTop: '1rem', color: '#6c757d', fontSize: '0.875rem' }}>
              Formatos permitidos: PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT, JPG, PNG, GIF
            </p>
          </FileUploadSection>

          {uploadedFiles && uploadedFiles.length > 0 && (
            <FileList>
              {uploadedFiles.map((file, index) => (
                <FileItem key={index}>
                  <div>
                    <strong>{file.name}</strong>
                    <br />
                    <small>{formatFileSize(file.size)}</small>
                  </div>
                  <RemoveFileButton onClick={() => removeFile(index)}>Eliminar</RemoveFileButton>
                </FileItem>
              ))}
            </FileList>
          )}
        </FormSection>

        <FormSection>
          <SectionTitle>Configuración de la Lección</SectionTitle>

          <FormField>
            <Checkbox
              label="Lección Activa"
              checked={inputs.status}
              onChange={() => handleCheckbox('status')}
            />
          </FormField>

          <FormField>
            <Checkbox
              label="Lección Bloqueada"
              checked={inputs.isLocked}
              onChange={() => handleCheckbox('isLocked')}
            />
          </FormField>

          <FormField>
            <Checkbox
              label="Requiere Completar Lección Anterior"
              checked={inputs.requiresCompletion}
              onChange={() => handleCheckbox('requiresCompletion')}
            />
          </FormField>
        </FormSection>

        <ButtonContainer>
          <Button onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" primary loading={loading} disabled={loading}>
            {lesson ? 'Actualizar Lección' : 'Crear Lección'}
          </Button>
        </ButtonContainer>
      </Form>
    </FormContainer>
  );
};

export default LessonForm;
